import { AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { Button } from 'base/ui/button';
import {
  defaultMeasureDraggedElement,
  MeasureDraggedElement,
} from 'base/ui/dnd';
import {
  IconAdd,
  IconHome,
  IconOption,
  IconPage,
  IconPageGroup,
} from 'base/ui/icons';
import { Locale } from 'base/ui/locales';
import { MenuButton } from 'base/ui/menu';
import { menuItem, MenuModel } from 'base/ui/menu_model';
import { ClickInTextarea, ClickInTextareaRef } from 'base/ui/textarea';
import { TextSmall } from 'base/ui/typography';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import classNames from 'classnames/bind';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { shortcut, useKeyboardShortcuts } from '../../keyboard_shortcuts';
import { NotificationsContainer, TaskNotifications } from '../ai/notifications';
import { PageEditingActions } from '../editing_actions/page';
import { ProjectEditingActions } from '../editing_actions/project';
import { EditorMode } from '../editor_mode';
import { Page, pageDomain, PageState, Project, Space } from '../model';
import { useSelectable } from '../selection/selectable';
import { SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { globalSuggestionsStore } from '../suggested_edits';
import styles from './pages.css';
import { useCommonPageInteractions, usePathEditMenu } from './page_common';
import { pageDndDomain, PagePosition } from './page_dnd';

type ContextMenu = {
  model: MenuModel;
  onContextMenu: ((e: React.MouseEvent) => void) | undefined;
  menuPortal: React.ReactNode;
};

const cx = classNames.bind(styles);

// Uncomment to show debug box outlines:
// document.body.dataset.debugSitemap = 'true';

export const showOnPageHoverClassName = styles.showOnHover;

function DropBeforeTarget({
  parent,
  index,
  editing,
  ...props
}: {
  parent: Page | Space;
  index: number;
  editing: ProjectEditingActions | undefined;
} & Pick<
  React.ComponentProps<typeof pageDndDomain.DropTarget>,
  'className' | 'style' | 'children'
>) {
  return (
    <pageDndDomain.DropTarget
      opts={
        editing && {
          mouseToDestination: () => ({ parent, index }),
          processDrop: editing.dropPageAt,
        }
      }
      {...props}
    />
  );
}

const SinglePageView = observer(function _SinglePageView({
  page,
  isHomePage,
  selection,
  selectionActions,
  analytics,
  SectionList,
  projectEditing,
  isSuggestion,
  flags,
}: {
  page: Page | PageState;
  isHomePage: boolean;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  SectionList: React.ComponentType<{ page: Page | PageState }>;
  projectEditing: ProjectEditingActions | undefined;
  isSuggestion: boolean;
  flags: FeatureFlagsStore;
}) {
  const topBarRef = React.useRef<HTMLDivElement>(null);
  const nameRef = React.useRef<ClickInTextareaRef>(null);
  const sectionListRef = React.useRef<HTMLDivElement>(null);

  const editing = pageDomain.isMutable(page)
    ? projectEditing?.forPage(page)
    : undefined;

  const [isSelected, selectableClassName] = pageDomain.isMutable(page)
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useSelectable(topBarRef, selection, selection.factory.forPage(page), {
        onSelect: projectEditing?.sidebarNavigation.openEditPanelIfClosed,
        attachFrameTo: sectionListRef,
      })
    : [false, ''];

  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter')]: () => nameRef.current?.focus(),
  });

  const pageEditing = page.mutable ? projectEditing?.forPage(page) : undefined;
  const showPathOption =
    page.mutable !== undefined &&
    !selection.factory.isRootPageOfSpace(page as Page);
  const extraMenuItems = editing
    ? [
        menuItem({
          label: 'Find page in wireframe',
          onClick: editing.makeWireframeVisible,
        }),
      ]
    : [];
  const [model, onContextMenu, menuPortal] = useCommonPageInteractions({
    projectEditing,
    pageEditing,
    selectionActions,
    analytics,
    isSelected,
    showPathOption,
    extraMenuItems,
  });
  return (
    <>
      <PagePathHeader
        page={page}
        projectEditing={projectEditing}
        isSuggestion={isSuggestion}
        isSelected={isSelected}
        selectableClassName=""
        menu={{ model, onContextMenu, menuPortal }}
        topBarRef={topBarRef}
        nameRef={nameRef}
        editing={editing}
        icon={isHomePage ? <IconHome /> : <IconPage />}
      >
        <div ref={sectionListRef} className={styles.sectionListContainer}>
          <SectionList page={page} />
        </div>
      </PagePathHeader>
    </>
  );
});

/**
 * Shared UI for page/path headers, and optionally a section list in the case of a page.
 */
const PagePathHeader = observer(function _PagePathHeader({
  page,
  projectEditing,
  isSuggestion,
  isSelected,
  selectableClassName,
  menu,
  topBarRef,
  nameRef,
  editing,
  children,
  icon,
}: {
  page: Page | PageState;
  projectEditing: ProjectEditingActions | undefined;
  isSuggestion: boolean;
  isSelected: boolean;
  selectableClassName: string;
  menu: ContextMenu;
  topBarRef: React.RefObject<HTMLDivElement>;
  nameRef: React.RefObject<ClickInTextareaRef>;
  editing: PageEditingActions | undefined;
  children?: React.ReactNode;
  icon: React.ReactNode;
}) {
  // TODO(meredith): Move this into Textarea, or make the textfield a form input.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.stopPropagation();
      e.preventDefault();
      e.currentTarget.blur();
    }
  };
  return (
    <div
      className={cx(
        styles.page,
        isSuggestion && styles.isSuggestion,
        selectableClassName,
      )}
      onContextMenu={menu.onContextMenu}
    >
      {menu.menuPortal}
      <NotificationsContainer offset="sitemap">
        {projectEditing && pageDomain.isMutable(page) && (
          <TaskNotifications
            editing={projectEditing}
            target={page}
            mode={EditorMode.Sitemap}
          />
        )}
      </NotificationsContainer>
      <div className={styles.pageContainer}>
        <div
          ref={topBarRef}
          className={cx(styles.pageHeader, isSelected && styles.isSelected)}
        >
          <div className={styles.pageHeaderIconAndText}>
            <div className={styles.pageHeaderIcon}>{icon}</div>
            <div className={styles.truncated}>
              <TextSmall>
                <ClickInTextarea
                  ref={nameRef}
                  enableClickIn={isSelected}
                  variant="popoutLight"
                  value={page.name}
                  onChange={editing?.setName}
                  placeholder="Untitled page"
                  onKeyDown={onKeyDown}
                  truncate
                />
              </TextSmall>
            </div>
          </div>
          {menu.model.length > 0 && (
            <MenuButton
              icon={IconOption}
              variant="tertiary"
              menu={menu.model}
            />
          )}
        </div>
        {children}
      </div>
    </div>
  );
});

/**
 * Paths have no section lists rendered, so we only render the header.
 */
const PathView = observer(function _PathView({
  page,
  selection,
  selectionActions,
  projectEditing,
  analytics,
}: {
  page: Page | PageState;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  projectEditing: ProjectEditingActions | undefined;
  analytics: AnalyticsService;
}) {
  const topBarRef = React.useRef<HTMLDivElement>(null);
  const nameRef = React.useRef<ClickInTextareaRef>(null);

  const editing = pageDomain.isMutable(page)
    ? projectEditing?.forPage(page)
    : undefined;

  const [isSelected, selectableClassName] = pageDomain.isMutable(page)
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useSelectable(topBarRef, selection, selection.factory.forPage(page), {
        onSelect: projectEditing?.sidebarNavigation.openEditPanelIfClosed,
        hideFrame: true,
      })
    : [false, ''];

  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter')]: () => nameRef.current?.focus(),
  });

  const pageEditing = page.mutable ? projectEditing?.forPage(page) : undefined;
  const [model, onContextMenu, menuPortal] = usePathEditMenu({
    page,
    selectionActions,
    pageEditing,
    analytics,
  });

  return (
    <PagePathHeader
      page={page}
      projectEditing={projectEditing}
      isSelected={isSelected}
      selectableClassName={selectableClassName}
      menu={{ model, onContextMenu, menuPortal }}
      topBarRef={topBarRef}
      nameRef={nameRef}
      editing={editing}
      isSuggestion={false}
      icon={<IconPageGroup />}
    />
  );
});

const DropPreview = observer(function _DropPreview({
  parent,
  index,
  editing,
}: PagePosition & { editing: ProjectEditingActions | undefined }) {
  const dndState = pageDndDomain.state.get();
  if (dndState?.dest?.parent !== parent || dndState.dest.index !== index)
    return null;
  return (
    <DropBeforeTarget
      parent={parent}
      index={index}
      editing={editing}
      className={styles.dropPreviewContainer}
      style={{ width: dndState.drag.width, height: dndState.drag.height }}
    >
      <div className={styles.dropPreview} />
    </DropBeforeTarget>
  );
});

const InsertPage = observer(function _InsertPage({
  parent,
  editing,
  analytics,
  index,
  insertDirection,
}: {
  parent: Page | Space | undefined;
  editing: ProjectEditingActions | undefined;
  analytics: AnalyticsService;
  index: number;
  insertDirection: 'before' | 'after' | 'below';
}) {
  if (parent == null || !editing) {
    return <div className={styles.insertPage} />;
  }

  return (
    <DropBeforeTarget
      parent={parent}
      index={index}
      editing={editing}
      className={cx(styles.insertPage, insertDirection)}
    >
      <div className={styles.insertPageButton}>
        <Button
          variant="tertiary"
          background="grey2"
          icon={IconAdd}
          onClick={() => {
            editing.addSubPage(parent, index);
            analytics.track({
              event: 'blocks/add-page',
              method: 'blank',
              direction: insertDirection,
            });
          }}
        />
      </div>
    </DropBeforeTarget>
  );
});

/** Displays a list of pages (horizontally) and their children, in a tree view */
export const PageListView = observer(function _PageListView({
  pages,
  parent,
  locale,
  selection,
  selectionActions,
  analytics,
  editing,
  SectionList,
  flags,
}: {
  pages: readonly Page[];
  /* If parent is undefined, pages should just be `[selection.project.homePage]` */
  parent: Page | Space | undefined;
  locale: Locale;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  SectionList: React.ComponentType<{ page: Page | PageState }>;
  flags: FeatureFlagsStore;
}) {
  // Duplicate the indexes here, so they aren't affected by adding a placeholders
  type PageListItem = {
    page: Page | PageState | 'placeholder';
    index: number;
    isSuggestion?: boolean;
    hasDraggedAway?: boolean;
  };
  const dndState = pageDndDomain.state.get();
  const pageOrPlaceholders = pages.map<PageListItem>((page, index) => ({
    page,
    index,
    hasDraggedAway: dndState?.source?.from?.mutable === page,
  }));
  if (dndState?.dest && dndState.dest.parent === parent) {
    const { index } = dndState.dest;
    pageOrPlaceholders.splice(index, 0, { page: 'placeholder', index });
  }
  const suggestions =
    parent && globalSuggestionsStore.getSuggestedPages(parent);
  for (const { page, index } of suggestions || []) {
    pageOrPlaceholders.splice(index, 0, {
      page,
      index,
      isSuggestion: true,
    });
  }

  const firstConnectorIndex = pageOrPlaceholders.findIndex(
    ({ hasDraggedAway }) => !hasDraggedAway,
  );
  const lastConnectorIndex = findLastIndex(
    pageOrPlaceholders,
    ({ hasDraggedAway }) => !hasDraggedAway,
  );

  const children = pageOrPlaceholders.map(
    ({ page, index, isSuggestion }, indexArr) => {
      const childIsPath = page !== 'placeholder' && page.pageType === 'path';
      return (
        <pageDndDomain.Draggable
          key={
            page === 'placeholder'
              ? `placeholder-${indexArr}`
              : keyOfObject(page)
          }
          source={
            // you can never drag the home page
            parent &&
            // can't drag in non editable mode
            pageDomain.isMutable(page) &&
            editing
              ? {
                  page: page.state,
                  from: {
                    parent,
                    index,
                    mutable: page,
                  },
                }
              : undefined
          }
          measureDraggedElement={measureDraggedPage}
          supportsDuplicate={true}
          selfDropTarget={parent && { parent, index }}
          onAfterDrop={editing?.onAfterDropPage}
          dragPreviewClass={styles.dragPreview}
          dropTarget={
            editing &&
            parent && {
              mouseToDestination: (clientX, _clientY, el) => {
                const box = el.getBoundingClientRect();
                const isLeft = clientX <= (box.left + box.right) / 2;
                return {
                  parent,
                  index: index + (isLeft ? 0 : 1),
                };
              },
              processDrop: editing.dropPageAt,
            }
          }
        >
          <div
            className={cx(
              styles.pageListPage,
              parent == null && styles.isHomePage,
            )}
          >
            {pageDomain.isMutable(parent) && (
              <ConnectorUpwards
                isFirst={indexArr === firstConnectorIndex}
                isLast={indexArr === lastConnectorIndex}
              />
            )}
            {page === 'placeholder' ? (
              parent && (
                <DropPreview parent={parent} index={index} editing={editing} />
              )
            ) : (
              <div className={styles.pageListPageTop}>
                <InsertPage
                  parent={parent}
                  index={index}
                  analytics={analytics}
                  insertDirection="before"
                  editing={editing}
                />
                {childIsPath ? (
                  <PathView
                    page={page}
                    selection={selection}
                    selectionActions={selectionActions}
                    projectEditing={editing}
                    analytics={analytics}
                  />
                ) : (
                  <SinglePageView
                    page={page}
                    isHomePage={
                      parent == null || isHomePage(selection.project, page)
                    }
                    selection={selection}
                    selectionActions={selectionActions}
                    analytics={analytics}
                    SectionList={SectionList}
                    projectEditing={editing}
                    isSuggestion={isSuggestion || false}
                    flags={flags}
                  />
                )}
                <InsertPage
                  parent={parent}
                  index={index + 1}
                  analytics={analytics}
                  insertDirection="after"
                  editing={editing}
                />
              </div>
            )}
            {page !== 'placeholder' &&
              page.mutable &&
              (page.subPages.length > 0 ||
              globalSuggestionsStore.getSuggestedPages(page).length > 0 ? (
                <>
                  <ConnectorDownwards />
                  <div className={styles.pageListPageBottom}>
                    <PageListView
                      pages={page.subPages}
                      parent={page}
                      locale={locale}
                      selection={selection}
                      selectionActions={selectionActions}
                      analytics={analytics}
                      SectionList={SectionList}
                      editing={editing}
                      flags={flags}
                    />
                  </div>
                </>
              ) : (
                <InsertSubPages
                  parent={page}
                  editing={editing}
                  analytics={analytics}
                />
              ))}
          </div>
        </pageDndDomain.Draggable>
      );
    },
  );

  return (
    <div className={styles.pageList} lang={locale}>
      {children}
    </div>
  );
});

const measureDraggedPage: MeasureDraggedElement = (el) => {
  // The outer div expands to fill the full height of the row.
  // We use the inner div, so that drag placeholders are shorter:
  const inner = el.querySelector(`.${styles.pageListPage}`);
  if (!(inner instanceof HTMLElement)) return defaultMeasureDraggedElement(el);
  return {
    width: inner.offsetWidth,
    height: inner.offsetHeight,
  };
};

const InsertSubPages = observer(function _InsertSubPages({
  parent,
  editing,
  analytics,
}: {
  parent: Page;
  editing: ProjectEditingActions | undefined;
  analytics: AnalyticsService;
}) {
  const dndState = pageDndDomain.state.get();
  const isHovering = dndState && dndState.dest?.parent === parent;
  const insertPage = (
    <InsertPage
      parent={parent}
      index={0}
      insertDirection="below"
      analytics={analytics}
      editing={editing}
    />
  );
  if (!isHovering) return insertPage;
  return (
    <DropBeforeTarget
      parent={parent}
      index={0}
      editing={editing}
      className={styles.insertSubPages}
    >
      {insertPage}
      <ConnectorDownwards />
      <ConnectorUpwards isFirst isLast />
      <DropPreview parent={parent} index={0} editing={editing} />
    </DropBeforeTarget>
  );
});

function ConnectorDownwards({ button }: { button?: React.ReactNode }) {
  return (
    <div className={cx(styles.connectorDownwards, button && styles.hasButton)}>
      {button && <div className={styles.connectorButtonWrapper}>{button}</div>}
    </div>
  );
}

function ConnectorUpwards({
  isFirst,
  isLast,
}: {
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div
      className={cx(
        styles.connectorUpwards,
        isFirst && styles.isFirst,
        isLast && styles.isLast,
      )}
    >
      <div className={styles.connectorHorizLine} />
      <div
        className={
          isFirst === isLast
            ? styles.connectorCenterOnly
            : isFirst
            ? styles.connectorCenterFirst
            : styles.connectorCenterLast
        }
      />
      <div className={styles.connectorHorizLine} />
    </div>
  );
}

function findLastIndex<T>(arr: readonly T[], predicate: (t: T) => boolean) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (predicate(arr[i])) return i;
  }
  return -1;
}

function isHomePage(project: Project, page: Page | PageState | 'placeholder') {
  if (project.homePage === page) {
    return true;
  }
  // The first page of every space is considered the "home" page.
  return project.spaces.some(
    (space: Space) => space.subPages.length > 0 && space.subPages[0] === page,
  );
}
